import React, { useEffect } from 'react';
import { SearchProvider } from '@/components/search/context/SearchContext';
import { SearchForm } from '@/components/search/components/SearchForm';
import { SearchLayout } from '@/components/search/SearchLayout';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { StructuredData, createBreadcrumbSchema, createFAQSchema } from '@/components/seo/StructuredData';
import { BreadcrumbNav } from '@/components/navigation/BreadcrumbNav';

const Search = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const page = searchParams.get('page') || '1';
  const costType = searchParams.get('cost_type') || 'avg_accommodation_cost';
  const filter = searchParams.get('filter') || '';
  
  // Create a page title based on search params with more enticing language
  const costTypeReadable = costType === 'avg_accommodation_cost' ? 'Accommodation' : 
                           costType === 'avg_meal_cost' ? 'Meals' : 
                           costType === 'avg_daily_budget' ? 'Daily Budget' : 
                           'Destinations';
  
  const pageTitle = searchQuery 
    ? `${searchQuery} - Top ${costTypeReadable} Options for 2025` 
    : `Explore ${costTypeReadable} - Best Vacation Options for 2025`;

  // Create a more compelling meta description with call-to-action
  const metaDescription = searchQuery
    ? `Discover the best ${costTypeReadable.toLowerCase()} options for "${searchQuery}" with detailed pricing, reviews, and local insights. Compare options and book your perfect ${searchQuery} adventure today!`
    : `Find the perfect ${costTypeReadable.toLowerCase()} for your dream vacation. Browse by price range, amenities, and traveler ratings. Plan and book with confidence using our insider travel guides.`;

  // Define keywords based on search params
  const getKeywords = () => {
    const baseKeywords = ['travel search', 'destinations', costTypeReadable.toLowerCase(), 'travel planning'];
    
    if (searchQuery) {
      return [
        ...baseKeywords,
        searchQuery,
        `${searchQuery} travel`,
        `${searchQuery} vacation`,
        `${searchQuery} ${costTypeReadable.toLowerCase()}`
      ];
    }
    
    return baseKeywords;
  };

  // Define breadcrumbs for search page
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Search', url: '/search', isCurrentPage: !searchQuery }
  ];

  // If search query is present, add it to breadcrumbs
  if (searchQuery) {
    breadcrumbs[1] = { 
      name: 'Search', 
      url: '/search'
    };
    
    // If we have a filter, add it between Search and the query
    if (filter) {
      breadcrumbs.push({
        name: filter.charAt(0).toUpperCase() + filter.slice(1),  // Capitalize filter name
        url: `/search?filter=${encodeURIComponent(filter)}`
      });
    }
    
    breadcrumbs.push({
      name: searchQuery,
      url: `/search?q=${encodeURIComponent(searchQuery)}`,
      isCurrentPage: true
    });
  }

  // Create breadcrumb schema
  const breadcrumbSchema = createBreadcrumbSchema(breadcrumbs.map(item => ({
    name: item.name,
    url: item.url
  })));

  // Create FAQ schema for search page using the helper function
  const faqData = [
    {
      question: `How do I find the best ${costTypeReadable.toLowerCase()} options${searchQuery ? ` in ${searchQuery}` : ''}?`,
      answer: `Use our advanced filters to narrow down ${costTypeReadable.toLowerCase()} options${searchQuery ? ` in ${searchQuery}` : ''} by price range, amenities, ratings, and location. Sort results by price or popularity to find your perfect match.`
    },
    {
      question: `What is the best time to visit${searchQuery ? ` ${searchQuery}` : ''}?`,
      answer: `The best time to visit depends on your preferences. Use our climate information to see which months offer ideal weather conditions. You can also check our seasonal pricing data to find the most affordable time to travel.`
    },
    {
      question: `How can I connect with local hosts${searchQuery ? ` in ${searchQuery}` : ''}?`,
      answer: `Travilis connects you with verified local hosts who can provide insider knowledge, personalized tours, and authentic experiences. Browse host profiles with ratings and reviews, and message them directly to plan your perfect trip.`
    }
  ];

  const faqSchema = createFAQSchema(faqData);

  useEffect(() => {
    // Log search parameters on component mount or update
    console.log('Search page with params:', Object.fromEntries(searchParams.entries()));
    
    // Update document title dynamically based on search query
    document.title = pageTitle + ' | travilis';
    
    return () => {
      console.log('Search page unmounted');
    };
  }, [searchParams, pageTitle]);

  return (
    <>
      <SEO
        title={pageTitle}
        description={metaDescription}
        keywords={getKeywords()}
        breadcrumbs={breadcrumbs}
      />
      
      <StructuredData schema={[breadcrumbSchema, faqSchema]} />
      
      <SearchProvider>
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNav items={breadcrumbs} />
          <SearchForm />
          <SearchLayout />
        </div>
      </SearchProvider>
    </>
  );
};

export default Search;
